/**
 * Admin — Listado y revisión de empresas (Carlos jun 28, 2026)
 * /admin/empresas
 *
 * Tabs: Pendientes / En revisión / Aprobadas / Rechazadas
 * Click en empresa → modal con docs (imágenes inline) + Aprobar/Rechazar
 * Botón "Comisión" abajo → modal para editar % global (default 1.5)
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import AdminLayout from '../components/AdminLayout';
import { BRAND } from '../brand';

const STATUS_TABS = [
  { key: 'pending_review', label: '🔎 En revisión', color: 'blue' },
  { key: 'pending_docs', label: '📤 Faltan docs', color: 'amber' },
  { key: 'approved', label: '✅ Aprobadas', color: 'emerald' },
  { key: 'rejected', label: '❌ Rechazadas', color: 'red' },
];

// Carlos jul 1, 2026 — preview de PDFs sin caer en el bloqueo de data: URL
// que Chrome/Firefox/Safari aplican. Convertimos b64 → Blob → blob: URL y lo
// embebemos en un <iframe>. Además 2 botones: Abrir nueva pestaña + Descargar.
const PdfPreview = ({ mime, b64, label }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);

  React.useEffect(() => {
    if (!b64) return;
    try {
      const byteChars = atob(b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch (e) {
      // base64 corrupto — no romper la UI
      setBlobUrl(null);
    }
  }, [b64, mime]);

  if (!blobUrl) {
    return <p className="text-[10px] text-rose-600 mt-1">⚠️ No se pudo procesar el PDF</p>;
  }

  return (
    <div className="mt-2">
      <iframe
        src={blobUrl}
        title={label || 'PDF preview'}
        className="w-full h-64 rounded border border-slate-300 bg-white"
      />
      <div className="flex gap-3 mt-1">
        <button
          type="button"
          onClick={() => window.open(blobUrl, '_blank')}
          className="text-xs text-emerald-700 hover:underline"
        >
          🔍 Abrir en pestaña
        </button>
        <a
          href={blobUrl}
          download={`${(label || 'documento').replace(/\s+/g, '_')}.pdf`}
          className="text-xs text-emerald-700 hover:underline"
        >
          📥 Descargar
        </a>
      </div>
    </div>
  );
};

const AdminEmpresas = () => {
  const [tab, setTab] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [openEmpresa, setOpenEmpresa] = useState(null); // detalle
  const [ordersEmpresa, setOrdersEmpresa] = useState(null); // envíos de una empresa (Carlos jul 3, 2026)
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/admin/empresas', { params });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-2xl font-black text-slate-900">🏢 Empresas</h1>
          <button
            onClick={() => setShowSettings(true)}
            data-testid="adm-emp-settings-btn"
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg"
          >
            ⚙️ Comisión empresa
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="adm-emp-tabs">
          {STATUS_TABS.map((t) => {
            const count = data.counts[t.key] ?? 0;
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`adm-emp-tab-${t.key}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${isActive ? 'bg-slate-900 text-white' : 'bg-white border border-slate-300 hover:bg-slate-50'}`}
              >
                {t.label} <span className={`ml-1 text-xs ${isActive ? 'opacity-80' : 'text-slate-500'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Buscador */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por razón social, RUT, email o representante…"
          data-testid="adm-emp-search"
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />

        {/* Listado */}
        {loading ? (
          <div className="text-center py-10 text-slate-500">Cargando…</div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-10 text-slate-500" data-testid="adm-emp-empty">
            No hay empresas en este estado.
          </div>
        ) : (
          <div className="space-y-2" data-testid="adm-emp-list">
            {data.items.map((e) => (
              <button
                key={e.id}
                onClick={() => setOpenEmpresa(e.id)}
                data-testid={`adm-emp-item-${e.id}`}
                className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:bg-emerald-50 hover:border-emerald-300 transition"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">
                      {e.razon_social}
                      {e.is_disabled && (
                        <span
                          className="ml-2 inline-block px-2 py-0.5 bg-red-100 text-red-800 text-[10px] font-bold rounded-full"
                          data-testid={`adm-emp-disabled-pill-${e.id}`}
                        >
                          🚫 DESACTIVADA
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-600">
                      RUT: <code>{e.rut}</code> · Rep: {e.rep_legal_name}
                    </p>
                    <p className="text-xs text-slate-500">
                      📧 {e.email} · 📱 {e.phone}
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    Registrada {e.created_at?.slice(0, 10)}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(ev) => { ev.stopPropagation(); setOrdersEmpresa(e); }}
                      data-testid={`adm-emp-orders-btn-${e.id}`}
                      className="block mt-1.5 px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-bold hover:bg-indigo-700 text-center"
                    >
                      📦 Ver envíos
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {openEmpresa && (
        <EmpresaDetailModal
          empresaId={openEmpresa}
          onClose={() => {
            setOpenEmpresa(null);
            load();
          }}
        />
      )}

      {/* Modal envíos de la empresa (Carlos jul 3, 2026) */}
      {ordersEmpresa && (
        <EmpresaOrdersModal empresa={ordersEmpresa} onClose={() => setOrdersEmpresa(null)} />
      )}

      {/* Modal settings */}
      {showSettings && (
        <CommissionSettingsModal onClose={() => setShowSettings(false)} />
      )}
    </AdminLayout>
  );
};

// =============================================================
// Modal detalle empresa
// =============================================================
const EmpresaDetailModal = ({ empresaId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [docPreviews, setDocPreviews] = useState({}); // {doc_type: {b64, mime}}
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/empresas/${empresaId}`).then((r) => setDetail(r.data));
  }, [empresaId]);

  const loadDoc = async (docType) => {
    if (docPreviews[docType]) return; // ya cargado
    try {
      const r = await api.get(`/admin/empresas/${empresaId}/doc/${docType}`);
      setDocPreviews((p) => ({ ...p, [docType]: r.data }));
    } catch {
      // ignore
    }
  };

  const approve = async () => {
    if (!window.confirm('¿Aprobar esta empresa? Podrá empezar a operar inmediatamente.')) return;
    setBusy(true);
    try {
      await api.post(`/admin/empresas/${empresaId}/approve`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 4) {
      alert('Ingresá un motivo claro (mín 4 caracteres)');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/empresas/${empresaId}/reject`, { reason: rejectReason.trim() });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (disableReason.trim().length < 4) {
      alert('Ingresá un motivo claro (mín 4 caracteres)');
      return;
    }
    if (!window.confirm(
      `⚠️ DESACTIVAR "${detail?.razon_social}"?\n\n` +
      `La empresa NO podrá loguear más, aunque tenga la contraseña correcta.\n` +
      `Usar solo para credenciales filtradas o abuso.\n\n` +
      `Se puede reactivar después desde este mismo panel.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/empresas/${empresaId}/disable`, { reason: disableReason.trim() });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    if (!window.confirm(`¿Reactivar "${detail?.razon_social}"? Volverá a poder loguear.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/empresas/${empresaId}/enable`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  if (!detail) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose} data-testid="adm-emp-detail-modal">
      <div
        className="bg-white max-w-3xl mx-auto rounded-2xl shadow-xl p-5 sm:p-7 my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-black text-slate-900">{detail.razon_social}</h2>
            <p className="text-xs text-slate-600 mt-1">
              RUT <code>{detail.rut}</code> · Rep: {detail.rep_legal_name}
            </p>
            <p className="text-xs text-slate-500">📧 {detail.email} · 📱 {detail.phone}</p>
          </div>
          <button onClick={onClose} className="text-2xl text-slate-400 hover:text-slate-700" data-testid="adm-emp-detail-close">×</button>
        </div>

        {/* IA extracted */}
        {detail.ai_extracted && Object.keys(detail.ai_extracted).length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-bold text-purple-800 mb-1">🤖 Datos detectados por IA en los documentos:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-purple-900">
              {Object.entries(detail.ai_extracted).map(([k, v]) => (
                <div key={k}>
                  <span className="font-semibold">{k.replaceAll('_', ' ')}:</span> {String(v)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documentos */}
        <h3 className="text-sm font-bold text-slate-800 mb-2">📂 Documentos cargados</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {Object.entries(detail.docs || {}).map(([docType, info]) => (
            <div key={docType} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
              <p className="text-xs font-semibold text-slate-700 mb-1">{docType.replaceAll('_', ' ')}</p>
              {info.uploaded ? (
                <>
                  <p className="text-[10px] text-slate-500">
                    {(info.size / 1024).toFixed(0)} KB · {info.mime} · {info.uploaded_at?.slice(0, 10)}
                  </p>
                  {!docPreviews[docType] ? (
                    <button
                      onClick={() => loadDoc(docType)}
                      data-testid={`adm-emp-doc-view-${docType}`}
                      className="mt-1 text-xs text-emerald-700 hover:underline"
                    >
                      👁️ Ver documento
                    </button>
                  ) : docPreviews[docType].mime?.startsWith('image/') ? (
                    <img
                      src={`data:${docPreviews[docType].mime};base64,${docPreviews[docType].b64}`}
                      alt={docType}
                      className="mt-1 max-h-48 rounded border border-slate-300"
                    />
                  ) : (
                    // Carlos jul 1, 2026 — PDFs no se pueden abrir con data: URL
                    // (los browsers modernos lo bloquean por seguridad). Usamos blob:
                    // que sí está permitido. Botones: "Ver inline" + "Descargar".
                    <PdfPreview
                      mime={docPreviews[docType].mime}
                      b64={docPreviews[docType].b64}
                      label={docType.replaceAll('_', ' ')}
                    />
                  )}
                </>
              ) : (
                <p className="text-[10px] text-slate-400 italic">No subido aún</p>
              )}
            </div>
          ))}
        </div>

        {/* Banner desactivada (feb 2026) */}
        {detail.is_disabled && (
          <div className="mb-3 bg-red-50 border-2 border-red-300 rounded-lg p-3" data-testid="adm-emp-disabled-banner">
            <p className="text-sm font-black text-red-900">🚫 EMPRESA DESACTIVADA</p>
            <p className="text-xs text-red-800 mt-1">
              <strong>Motivo:</strong> {detail.disabled_reason || '—'}
            </p>
            <p className="text-[10px] text-red-700 mt-0.5">
              Desactivada el {detail.disabled_at?.slice(0, 10)} por {detail.disabled_by}. No puede loguear.
            </p>
            <button
              onClick={enable}
              disabled={busy}
              data-testid="adm-emp-enable-btn"
              className="mt-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold rounded"
            >
              ♻️ Reactivar empresa
            </button>
          </div>
        )}

        {/* Acciones aprobar/rechazar (solo si NO desactivada) */}
        {!detail.is_disabled && detail.status !== 'approved' && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
            <button
              onClick={approve}
              disabled={busy}
              data-testid="adm-emp-approve-btn"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-2 rounded-lg font-semibold"
            >
              ✅ Aprobar empresa
            </button>
            <button
              onClick={() => setShowReject(!showReject)}
              data-testid="adm-emp-reject-toggle"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold"
            >
              ❌ Rechazar
            </button>
          </div>
        )}

        {showReject && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-1">Motivo del rechazo (visible al admin):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              data-testid="adm-emp-reject-reason"
              className="w-full text-sm p-2 border border-red-300 rounded"
              placeholder="Ej: Carpeta tributaria no corresponde al RUT informado…"
            />
            <button
              onClick={reject}
              disabled={busy}
              data-testid="adm-emp-reject-confirm"
              className="mt-2 px-3 py-1.5 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold rounded"
            >
              Confirmar rechazo
            </button>
          </div>
        )}

        {detail.status === 'approved' && !detail.is_disabled && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
            ✅ Empresa <strong>aprobada</strong> el {detail.approved_at?.slice(0, 10)} por {detail.approved_by}
          </div>
        )}

        {/* Botón Desactivar (siempre disponible mientras NO esté ya desactivada) — feb 2026 */}
        {!detail.is_disabled && (
          <div className="mt-4 pt-3 border-t border-slate-200">
            {!showDisable ? (
              <button
                onClick={() => setShowDisable(true)}
                data-testid="adm-emp-disable-toggle"
                className="w-full bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-lg text-sm font-semibold"
              >
                🚫 Desactivar empresa (bloqueo permanente de login)
              </button>
            ) : (
              <div className="bg-slate-50 border border-slate-300 rounded-lg p-3">
                <p className="text-xs font-bold text-slate-800 mb-1">
                  Motivo de la desactivación (queda en el audit log):
                </p>
                <textarea
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  rows={2}
                  data-testid="adm-emp-disable-reason"
                  className="w-full text-sm p-2 border border-slate-400 rounded"
                  placeholder="Ej: Credenciales expuestas, cuenta de prueba, abuso detectado…"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={disable}
                    disabled={busy}
                    data-testid="adm-emp-disable-confirm"
                    className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white text-sm font-bold rounded"
                  >
                    🚫 Confirmar desactivación
                  </button>
                  <button
                    onClick={() => { setShowDisable(false); setDisableReason(''); }}
                    className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm rounded"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================
// Modal envíos de una empresa — Carlos jul 3, 2026
// KPIs + lista completa + detalle expandible + comprobante
// =============================================================
const ORDER_STATUS_UI = {
  pending_deposit: ['⏳ Esperando depósito', 'bg-amber-100 text-amber-800'],
  payment_uploaded: ['📎 Comprobante subido', 'bg-blue-100 text-blue-800'],
  verifying: ['🔍 Verificando', 'bg-blue-100 text-blue-800'],
  paid: ['💰 Pago verificado', 'bg-indigo-100 text-indigo-800'],
  processing: ['⚙️ Procesando', 'bg-indigo-100 text-indigo-800'],
  completed: ['✅ Completado', 'bg-emerald-100 text-emerald-800'],
  rejected: ['❌ Rechazado', 'bg-red-100 text-red-800'],
  cancelled: ['🚫 Cancelado', 'bg-slate-100 text-slate-600'],
};

const fmtClp = (x) => `$${Math.round(x || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}`;
const fmtBob = (x) => `Bs ${Math.round(x || 0).toLocaleString('es-BO')}`;
// Carlos jul 6, 2026 — fechas en hora chilena, formato día/mes/año 24h
const fmtFechaCl = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const p = new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
  } catch { return iso.slice(0, 16).replace('T', ' '); }
};

const EmpresaOrdersModal = ({ empresa, onClose }) => {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState(null); // order id expandido
  const [comprobante, setComprobante] = useState(null); // {order_id, b64, mime}

  useEffect(() => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get(`/admin/empresas/${empresa.id}/orders`, { params }).then((r) => setData(r.data));
  }, [empresa.id, statusFilter]);

  const verComprobante = async (orderId) => {
    if (comprobante?.order_id === orderId) { setComprobante(null); return; }
    try {
      const r = await api.get(`/admin/empresas/${empresa.id}/orders/${orderId}/comprobante`);
      setComprobante({ order_id: orderId, b64: r.data.image_base64, mime: r.data.mime });
    } catch {
      alert('Esta orden no tiene comprobante');
    }
  };

  const stats = data?.stats;
  const [settling, setSettling] = useState(false);

  // Carlos jul 8, 2026 — liquidar comisiones pendientes del partner (BOB→CLP)
  const liquidar = async () => {
    const pc = stats?.partner_commission || {};
    if (!window.confirm(
      `¿Confirmas que ya le transferiste ${fmtClp(pc.pending_clp)} CLP a ${empresa.razon_social}?\n\n` +
      `Se marcarán ${pc.pending_count} comisiones como PAGADAS (queda registrado quién y cuándo).`
    )) return;
    setSettling(true);
    try {
      const r = await api.post(`/admin/empresas/${empresa.id}/partner-commissions/settle`);
      alert(`✅ ${r.data.settled_orders} comisiones liquidadas`);
      const params = statusFilter ? { status: statusFilter } : {};
      const rr = await api.get(`/admin/empresas/${empresa.id}/orders`, { params });
      setData(rr.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al liquidar');
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white w-full max-w-3xl rounded-2xl shadow-xl my-4 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="adm-emp-orders-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-black text-slate-900">📦 Envíos — {empresa.razon_social}</h2>
            <p className="text-xs text-slate-500">RUT {empresa.rut} · {empresa.email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl font-bold px-2" data-testid="adm-emp-orders-close">✕</button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {!data ? (
            <div className="text-center py-10 text-slate-500">Cargando…</div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="adm-emp-orders-kpis">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-[10px] text-slate-500 font-semibold">📦 Total envíos</p>
                  <p className="text-xl font-black text-slate-900">{stats.total_orders}</p>
                  <p className="text-[10px] text-slate-500">✅ {stats.completed} · ⏳ {stats.pending}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="text-[10px] text-emerald-700 font-semibold">🇨🇱→🇧🇴 ({stats.clp_bob.count})</p>
                  <p className="text-sm font-black text-slate-900">{fmtClp(stats.clp_bob.sent_clp)} CLP</p>
                  <p className="text-[10px] text-emerald-700">→ {fmtBob(stats.clp_bob.delivered_bob)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-[10px] text-blue-700 font-semibold">🇧🇴→🇨🇱 ({stats.bob_clp.count})</p>
                  <p className="text-sm font-black text-slate-900">{fmtBob(stats.bob_clp.sent_bob)}</p>
                  <p className="text-[10px] text-blue-700">→ {fmtClp(stats.bob_clp.delivered_clp)} CLP</p>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3 flex flex-col justify-between">
                  <p className="text-[10px] text-indigo-700 font-semibold">⚙️ Gestión</p>
                  <button
                    onClick={() => window.open('/admin/transfer-orders', '_blank')}
                    className="text-[11px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-2 py-1.5 mt-1"
                    data-testid="adm-emp-orders-goto-p2p"
                  >
                    Ir a Envíos P2P →
                  </button>
                </div>
              </div>

              {/* 💰 Comisiones del partner por pagar (Carlos jul 8, 2026) */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center justify-between gap-3" data-testid="adm-emp-commission-kpi">
                <div>
                  <p className="text-[10px] text-amber-700 font-semibold">💰 Comisión partner (Bolivia→Chile)</p>
                  <p className="text-sm font-black text-slate-900">
                    Por pagar: {fmtClp(stats.partner_commission?.pending_clp || 0)} CLP
                    <span className="text-[10px] font-semibold text-slate-500"> · {stats.partner_commission?.pending_count || 0} envíos</span>
                  </p>
                  <p className="text-[10px] text-slate-500">Ya pagado: {fmtClp(stats.partner_commission?.paid_clp || 0)} CLP</p>
                </div>
                {(stats.partner_commission?.pending_clp || 0) > 0 && (
                  <button
                    onClick={liquidar}
                    disabled={settling}
                    data-testid="adm-emp-settle-btn"
                    className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold rounded-lg text-sm whitespace-nowrap"
                  >
                    {settling ? 'Liquidando…' : '✅ Liquidar ahora'}
                  </button>
                )}
              </div>

              {/* Filtro estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="adm-emp-orders-status-filter"
                className="mb-3 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
              >
                <option value="">Todos los estados</option>
                {Object.entries(ORDER_STATUS_UI).map(([k, [label]]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>

              {/* Lista de órdenes */}
              {data.items.length === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="adm-emp-orders-empty">Sin envíos.</div>
              ) : (
                <div className="space-y-2" data-testid="adm-emp-orders-list">
                  {data.items.map((o) => {
                    const [stLabel, stCls] = ORDER_STATUS_UI[o.status] || [o.status, 'bg-slate-100 text-slate-600'];
                    const isBobClp = o.direction === 'bob_clp';
                    const isOpen = expanded === o.id;
                    return (
                      <div key={o.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`adm-emp-order-${o.id}`}>
                        <button
                          onClick={() => { setExpanded(isOpen ? null : o.id); setComprobante(null); }}
                          className="w-full text-left p-3 hover:bg-slate-50 transition"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-mono font-bold text-slate-700">
                                {o.short_no}
                                <span className="ml-2 font-sans font-normal text-slate-400">{fmtFechaCl(o.created_at)}</span>
                              </p>
                              <p className="text-sm font-bold text-slate-900 mt-0.5">
                                {isBobClp
                                  ? <>🇧🇴 {fmtBob(o.amount_bob)} → 🇨🇱 {fmtClp(o.amount_clp)} CLP</>
                                  : <>🇨🇱 {fmtClp(o.amount_clp)} CLP → 🇧🇴 {fmtBob(o.amount_bob)}</>}
                              </p>
                              <p className="text-xs text-slate-600 truncate">
                                → {o.recipient?.nombre} · {o.recipient?.banco}
                                {o.recipient_qr && <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-bold">📱 QR</span>}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${stCls}`}>{stLabel}</span>
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-3 pb-3 bg-slate-50 border-t border-slate-100 text-xs space-y-1.5 pt-2" data-testid={`adm-emp-order-detail-${o.id}`}>
                            <p><span className="text-slate-500">Destinatario:</span> <strong>{o.recipient?.nombre}</strong>
                              {o.recipient?.rut && <> · RUT {o.recipient.rut}</>}
                              {o.recipient?.ci && <> · CI {o.recipient.ci}</>}
                            </p>
                            <p><span className="text-slate-500">Cuenta:</span> {o.recipient?.banco} · {o.recipient?.cuenta} ({o.recipient?.tipo_cuenta})</p>
                            {o.proposito && <p><span className="text-slate-500">Propósito:</span> {o.proposito}</p>}
                            <p>
                              <span className="text-slate-500">Tasa:</span> {Number(o.effective_rate || 0).toFixed(isBobClp ? 2 : 6)}
                              {' · '}<span className="text-slate-500">Comisión:</span> {o.commission_percent}%
                              {' · '}<span className="text-slate-500">Fee:</span> {isBobClp ? fmtBob(o.fee_bob) : `${fmtClp(o.fee_clp)} CLP`}
                            </p>
                            {isBobClp && o.metodo_pago && (
                              <p><span className="text-slate-500">Pagó vía:</span> {o.metodo_pago === 'qr_bolivia' ? '📱 QR Banco Económico' : '🏦 Transferencia'}</p>
                            )}
                            <div className="flex flex-wrap gap-2 pt-1">
                              {o.comprobante && (
                                <button
                                  onClick={() => verComprobante(o.id)}
                                  className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-bold"
                                  data-testid={`adm-emp-order-comprobante-${o.id}`}
                                >
                                  {comprobante?.order_id === o.id ? '✕ Ocultar comprobante' : '🧾 Ver comprobante depósito'}
                                </button>
                              )}
                              <button
                                onClick={() => window.open('/admin/transfer-orders', '_blank')}
                                className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold"
                              >
                                ⚙️ Gestionar en P2P
                              </button>
                            </div>
                            {comprobante?.order_id === o.id && (
                              comprobante.mime === 'application/pdf'
                                ? <PdfPreview mime={comprobante.mime} b64={comprobante.b64} label={`Comprobante ${o.short_no}`} />
                                : <img
                                    src={`data:${comprobante.mime};base64,${comprobante.b64}`}
                                    alt="Comprobante"
                                    className="mt-2 max-h-80 rounded-lg border border-slate-300 bg-white"
                                    data-testid={`adm-emp-order-comprobante-img-${o.id}`}
                                  />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================
// Modal settings comisión
// =============================================================
const CommissionSettingsModal = ({ onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [val, setVal] = useState('');
  const [resellVal, setResellVal] = useState('');
  const [spreadVal, setSpreadVal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/empresas/settings/config').then((r) => {
      setCfg(r.data);
      setVal(String(r.data.commission_pct));
      setResellVal(String(r.data.resell_pct ?? 1.4));
      setSpreadVal(String(r.data.spread_pct ?? 0));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/empresas/settings/config', {
        commission_pct: Number(val),
        resell_pct: Number(resellVal),
        spread_pct: Number(spreadVal),
      });
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full rounded-2xl shadow-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="adm-emp-settings-modal">
        <h2 className="text-lg font-black text-slate-900 mb-2">⚙️ Comisión INTERNA empresa</h2>
        <p className="text-xs text-slate-600 mb-4">
          % que {BRAND.name} cobra a la empresa <strong>(no visible para ella)</strong>. La cotización
          pública que ve la empresa usa los tiers normales (igual que cliente). La diferencia entre
          el tier público y este % es el margen que {BRAND.name} liquida con la empresa.
          <br />Default sugerido: <strong>1,5%</strong>.
        </p>
        {!cfg ? (
          <div>Cargando…</div>
        ) : (
          <>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">% de comisión (0–20)</span>
              <input
                type="number"
                step="0.1"
                min={0}
                max={20}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                data-testid="adm-emp-commission-input"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-base"
              />
            </label>
            <label className="block mt-4">
              <span className="text-xs font-semibold text-slate-700">💼 % reventa del partner (cotizador para SU cliente)</span>
              <input
                type="number"
                step="0.1"
                min={0}
                max={20}
                value={resellVal}
                onChange={(e) => setResellVal(e.target.value)}
                data-testid="adm-emp-resell-input"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-base"
              />
              <span className="text-[10px] text-slate-500 block mt-1">
                % fijo que se descuenta en el "Cotizador para tu cliente" del panel empresa.
                La empresa <strong>no lo ve</strong> — solo escribe el monto que cobra. Default: 1,4%.
              </span>
            </label>
            <label className="block mt-4">
              <span className="text-xs font-semibold text-slate-700">📉 Spread % sobre la tasa (0–10)</span>
              <input
                type="number"
                step="0.1"
                min={0}
                max={10}
                value={spreadVal}
                onChange={(e) => setSpreadVal(e.target.value)}
                data-testid="adm-emp-spread-input"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-base"
              />
              <span className="text-[10px] text-slate-500 block mt-1">
                Empeora la tasa de cambio del módulo Empresas en este % (ambas direcciones).
                Ganancia extra de {BRAND.name}, <strong>invisible para el partner</strong>. Ej: tasa real
                91,37 con spread 1% → el destinatario recibe a 90,46. Default: 0% (sin spread).
              </span>
            </label>
            {cfg.updated_at && (
              <p className="text-[10px] text-slate-500 mt-2">
                Último cambio: {fmtFechaCl(cfg.updated_at)} por {cfg.updated_by}
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="adm-emp-commission-save"
                className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminEmpresas;
